import { useEffect, useState } from "react";
import { DailyReturn } from "@vra/schema";
import { signArtefact, type CanonicalValue } from "./sign-compat";
import { TripSheetForm } from "./trip-sheet-form";
import { enqueue, listQueue, QueuedReturn } from "./db";
import { getDeviceKeys, getOfficerRef, setOfficerRef } from "./keys";
import { syncQueue } from "./sync";
import { InfoTip } from "./info-tip";
import { Tour, type TourStep } from "./tour";

const SETUP_TOUR: TourStep[] = [
  {
    selector: '[data-tour="officer-setup"]',
    title: "One-time device setup",
    body: "Before filing trip sheets on this device, enter your officer ID. It's bound to this device's signing key so every return you file is verifiably yours — without putting your name on the ledger.",
  },
];

const MAIN_TOUR: TourStep[] = [
  { selector: ".top-nav", title: "Welcome to VRA Trip Sheet", body: "This is the depot's digital counterpart to the paper trip sheet — Channel A of the two-channel verification system. It works offline; everything you file is queued and signed on this device." },
  { selector: '[data-tour="header-particulars"]', title: "Header particulars", body: "Bus, route, shift, date, odometer and battery State of Charge (SOC) readings. Look for the small \"i\" icons next to fields like SOC OUT/IN if you're unsure what a field means." },
  { selector: '[data-tour="crew-particulars"]', title: "Crew particulars", body: "Driver and conductor are recorded as opaque officer IDs, never names — identity is deliberately kept off the ledger." },
  { selector: '[data-tour="trips-table"]', title: "Trips", body: "One row per leg of the shift. Add a row for each trip with its SOC, kilometres, and Time Keeper acknowledgement." },
  { selector: '[data-tour="totals"]', title: "Totals", body: "Scheduled vs. actual kilometres. Actual km is what gets checked against the bus's own telemetry after you submit — never shown here beforehand, so the two channels stay independent." },
  { selector: '[data-tour="checklist"]', title: "Pre-departure checklist", body: "The physical inspection checklist — brakes, tyres, body condition, and so on — completed before the bus leaves the depot." },
  { selector: '[data-tour="memo-duty"]', title: "Memo & duty", body: "When the duty memo was given and taken, and whether the crew completed duty as scheduled." },
  { selector: '[data-tour="submit-button"]', title: "Sign & queue", body: "This signs the return with your device key and queues it. If you're offline it stays queued and syncs automatically once connectivity returns." },
  { selector: '[data-tour="queue"]', title: "Queue", body: "Everything filed from this device, with sync and reconciliation status. Sent-back returns can be corrected and re-filed straight from this list." },
];

// Phase I pilot depot — becomes server-driven configuration in M3.
const DEPOT_ID = "DL-DEP-001";
const OPERATOR_ENTITY = "PilotOperatorLtd";

interface ServerDaily {
  key: string;
  version: number;
  status: "UNRECONCILED" | "RECONCILED" | "FLAGGED" | "ACCEPTED_WITH_JUSTIFICATION" | "SENT_BACK" | "SUPERSEDED";
  varianceBps?: number;
  itmsKm?: number;
  sendBackReason?: string;
  signed: { digestSha256Hex: string };
}

export function App() {
  const [officer, setOfficer] = useState<string>("");
  const [officerSaved, setOfficerSaved] = useState(false);
  const [publicKey, setPublicKey] = useState<string>("");
  const [queue, setQueue] = useState<QueuedReturn[]>([]);
  const [syncMsg, setSyncMsg] = useState<string>("");
  const [serverRecords, setServerRecords] = useState<Map<string, ServerDaily>>(new Map());
  const [reFile, setReFile] = useState<DailyReturn | null>(null);
  const [tourActive, setTourActive] = useState(false);

  async function refreshQueue() {
    setQueue(await listQueue());
    // Post-submission disclosure ONLY (spec §4.3-1): the variance and the
    // ITMS reading are fetched for already-submitted returns, never shown
    // on the filing form.
    try {
      const res = await fetch("/api/v1/daily-returns");
      if (res.ok) {
        const list = (await res.json()) as ServerDaily[];
        setServerRecords(new Map(list.map((r) => [r.signed.digestSha256Hex, r])));
      }
    } catch {
      /* offline — disclosure panel simply stays empty */
    }
  }

  useEffect(() => {
    (async () => {
      const keys = await getDeviceKeys();
      setPublicKey(keys.publicKeyHex);
      const ref = await getOfficerRef();
      if (ref) {
        setOfficer(ref);
        setOfficerSaved(true);
      }
      await refreshQueue();
    })();
  }, []);

  async function saveOfficer() {
    if (!officer.trim()) return;
    // Dev convenience: self-register the device key. In production the
    // registrar performs RegisterOfficer after ePramaan attestation (§6.3).
    try {
      const keys = await getDeviceKeys();
      const res = await fetch("/api/v1/officers", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          officerId: officer.trim(),
          role: "DEPOT_OPERATOR",
          publicKeyHex: keys.publicKeyHex,
        }),
      });
      if (!res.ok && res.status !== 409) {
        setSyncMsg(`Registration failed: ${(await res.json()).error}`);
        return;
      }
    } catch {
      setSyncMsg("Backend unreachable — officer saved locally; register the key before syncing.");
    }
    await setOfficerRef(officer.trim());
    setOfficerSaved(true);
  }

  async function handleSubmit(r: DailyReturn) {
    const keys = await getDeviceKeys();
    // Dual signature at capture: content + timestamp fixed before upload (§12.3).
    const signed = await signArtefact(r as unknown as CanonicalValue, keys, officer);
    await enqueue({
      signed,
      queuedAt: new Date().toISOString(),
      syncState: "PENDING",
    });
    await refreshQueue();
    // Opportunistic sync — harmless offline, queue survives.
    await doSync();
  }

  async function doSync() {
    const res = await syncQueue();
    setSyncMsg(
      res.offline
        ? "Offline — returns stay queued and signed; will sync when connectivity resumes."
        : `Synced ${res.synced}/${res.attempted}${res.rejected ? `, ${res.rejected} rejected` : ""}.`,
    );
    await refreshQueue();
  }

  return (
    <div className="app">
      <nav className="top-nav">
        <span className="brand"><span className="mark">●</span> VRA · Trip Sheet</span>
        <span className="actions">
          <button className="btn-secondary" onClick={() => setTourActive(true)}>Take a tour</button>
          <span className="badge">{DEPOT_ID}</span>
        </span>
      </nav>
      <Tour steps={officerSaved ? MAIN_TOUR : SETUP_TOUR} active={tourActive} onClose={() => setTourActive(false)} />

      {!officerSaved ? (
        <section className="card" data-tour="officer-setup">
          <h2>Officer setup</h2>
          <p className="eyebrow">One-time on this device. Your ID is bound to the device key via RegisterOfficer (§6.3).</p>
          <div className="grid">
            <div>
              <label>Officer ID (opaque — no names on the ledger)
                <InfoTip text="Your officer identifier, e.g. OFF-1042 — not your name. This gets bound to this device's signing key so every return you file is traceable to you without exposing your identity on the ledger." />
              </label>
              <input value={officer} onChange={(e) => setOfficer(e.target.value)} placeholder="OFF-…" />
            </div>
          </div>
          <p><label>Device public key (share with the registrar)
              <InfoTip text="This device's cryptographic public key. Share it with the registrar so they can bind it to your officer ID via RegisterOfficer — this is what makes your signed returns verifiable." />
            </label>
            <span className="mono">{publicKey}</span></p>
          <button className="btn-primary" onClick={saveOfficer}>Save officer ID</button>
        </section>
      ) : (
        <>
          <p className="eyebrow">
            Filing as <span className="mono">{officer}</span> — ITMS readings for this
            period are disclosed only after submission (§4.3)
          </p>
          <h1>Daily Trip Sheet</h1>
          {reFile && (
            <p className="eyebrow">
              Re-filing {reFile.date} · {reFile.busRegistrationNo} as version {reFile.version} —
              the superseded version stays on the ledger (§4.1.2)
            </p>
          )}
          <TripSheetForm
            key={reFile ? `refile-${reFile.date}-${reFile.version}` : "new"}
            depotId={DEPOT_ID}
            operatorEntity={OPERATOR_ENTITY}
            initial={reFile ?? undefined}
            onSubmit={async (r) => {
              await handleSubmit(r);
              setReFile(null);
            }}
          />

          <section className="card" data-tour="queue">
            <h2>Queue</h2>
            {queue.length === 0 && <p>No returns on this device yet.</p>}
            {queue.map((q) => {
              const a = q.signed.artefact as {
                date?: string;
                busRegistrationNo?: string;
                actualKm?: number;
              };
              const server = serverRecords.get(q.signed.digestSha256Hex);
              return (
                <div className="queue-row" key={q.id}>
                  <span>
                    <span className="mono">{a.date} · {a.busRegistrationNo}</span>
                  </span>
                  <span className="mono" title={q.signed.digestSha256Hex}>
                    {q.signed.digestSha256Hex.slice(0, 12)}…
                  </span>
                  {/* Variance disclosure — after submission only (§4.3-1).
                      RECONCILED gets the sole semantic green; FLAGGED stays
                      surface-lifted per DESIGN.md (decision D-UI1). */}
                  {server ? (
                    <span>
                      {server.status === "SENT_BACK" && (
                        <button
                          className="btn-secondary"
                          style={{ marginRight: 8 }}
                          title={server.sendBackReason}
                          onClick={() =>
                            setReFile({
                              ...(q.signed.artefact as unknown as DailyReturn),
                              version: server.version + 1,
                              captureTimestamp: "",
                            })
                          }
                        >
                          Re-file (reason: {server.sendBackReason ?? "—"})
                        </button>
                      )}
                      <span className={`badge ${server.status === "RECONCILED" ? "success" : ""}`}>
                        {server.status}
                      </span>
                      {server.varianceBps !== undefined && (
                        <span className="mono" style={{ marginLeft: 8 }}>
                          op {a.actualKm} / itms {server.itmsKm} km · {server.varianceBps} bps
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className={`badge ${q.syncState === "SYNCED" ? "success" : ""}`}>
                      {q.syncState}
                    </span>
                  )}
                </div>
              );
            })}
            <div className="actions" style={{ marginTop: 16 }}>
              <button className="btn-secondary" onClick={doSync}>Sync now</button>
              {syncMsg && <span className="eyebrow" style={{ alignSelf: "center" }}>{syncMsg}</span>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
